import React, { useState, useEffect } from 'react';
import { Settings, UserPlus, Users, ChevronDown, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const SettingsMenu = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [username, setUsername] = useState('');
  const [projectType, setProjectType] = useState('faq');
  const [projectId, setProjectId] = useState('');
  const [message, setMessage] = useState('');
  const [projects, setProjects] = useState({ faq: [], manual: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchProjects();
    }
  }, [isOpen]);

  const fetchProjects = async () => {
    try {
      const [faqResponse, manualResponse] = await Promise.all([
        axios.get('/api/faq/my'),
        axios.get('/api/manual')
      ]);
      
      setProjects({
        faq: faqResponse.data,
        manual: manualResponse.data
      });
    } catch (error) {
      console.error('Ошибка получения проектов:', error);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!username.trim() || !projectId) {
      return;
    }

    setLoading(true);
    try {
      await axios.post('/api/collaboration/invite', {
        username: username.trim(),
        projectType,
        projectId,
        message: message.trim()
      });
      
      setShowInviteModal(false);
      setUsername('');
      setMessage('');
      setProjectId('');
    } catch (error) {
      console.error('Ошибка отправки приглашения:', error);
      alert(error.response?.data?.message || 'Ошибка отправки приглашения');
    } finally {
      setLoading(false);
    }
  };

  const getProjectName = (project) => {
    return project.title || project.name || 'Без названия';
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors px-2 py-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
      >
        <User className="h-4 w-4" />
        <span className="text-xs font-medium">{user?.username}</span>
        <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border dark:border-gray-700 z-50">
          <div className="p-2">
            <button
              onClick={() => {
                setShowInviteModal(true);
                setIsOpen(false);
              }}
              className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
            >
              <UserPlus className="h-4 w-4" />
              <span>Пригласить пользователя</span>
            </button>
          </div>
        </div>
      )}

      {/* Модальное окно приглашения */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Пригласить пользователя в проект
            </h3>
            
            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Имя пользователя
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Введите имя пользователя"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Тип проекта
                </label>
                <select
                  value={projectType}
                  onChange={(e) => {
                    setProjectType(e.target.value);
                    setProjectId('');
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="faq">FAQ</option>
                  <option value="manual">Руководство</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Проект
                </label>
                <select
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                  required
                >
                  <option value="">Выберите проект</option>
                  {projects[projectType]?.map((project) => (
                    <option key={project._id} value={project._id}>
                      {getProjectName(project)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Сообщение (необязательно)
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                  rows={3}
                  placeholder="Добавьте сообщение к приглашению"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 rounded-md transition-colors"
                >
                  {loading ? 'Отправка...' : 'Отправить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsMenu;
